/**
 * SessionManager.js
 * Client-side session management that works with Upstash Redis
 */

import EnhancedSecurity from './EnhancedSecurity';

class SessionManager {
  constructor() {
    // Generate cryptographically derived key names based on domain
    const domainHash = this.generateKeyName(window.location.hostname);
    this.sessionIdKey = `_${domainHash.substring(0, 8)}_s`;
    this.userIdKey = `_${domainHash.substring(8, 16)}_u`;
    this.apiEndpoint = 'https://devapi.rezcomm.com/device/remember';
    this.appSecret = this.deriveAppSecret();
  }
  
  /**
   * Generate a secure, unpredictable key name based on input
   * @param {string} input Base string to derive key from
   * @returns {string} Secure key name
   */
  generateKeyName(input) {
    // Use multiple sources of entropy instead of a fixed salt
    // This makes the keys unpredictable without storing a secret
    const entropy = [
      // Domain-specific data
      input,
      document.domain,
      // Browser-specific data that's hard to spoof
      navigator.userAgent.substring(0, 10),
      navigator.language,
      // Hardware-specific data
      screen.colorDepth,
      `${screen.width}x${screen.height}`,
      // Time-based component (changes periodically)
      Math.floor(Date.now() / (86400 * 1000)), // Changes daily
      // App-specific identifier
      'RezComm_v2_' + (process.env.VUE_APP_CHL)
    ].join('|');
    
    // Use a stronger hashing algorithm via EnhancedSecurity
    try {
      const hash = EnhancedSecurity.hashData(entropy);
      return hash.substring(0, 16);
    } catch (e) {
      // Fallback to simple hash if EnhancedSecurity is not available
      let hash = 0;
      for (let i = 0; i < entropy.length; i++) {
        const char = entropy.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash;
      }
      return Math.abs(hash).toString(36) + Date.now().toString(36).substring(4);
    }
  }

  /**
   * Derive a domain-specific secret key
   * @returns {string} Derived secret key
   */
  deriveAppSecret() {
    // Combine domain with browser-specific info that's hard to replicate across tabs
    const domain = window.location.hostname;
    const userAgent = navigator.userAgent.substring(0, 20);
    const screenProps = `${window.screen.width}x${window.screen.height}`;
    
    // Create a hash of these values
    return EnhancedSecurity.hashData(`${domain}-${userAgent}-${screenProps}-v1`);
  }

  /**
   * Store a value securely in localStorage with additional protection
   * @param {string} key Storage key
   * @param {string} value Value to store
   */
  storeSecureValue(key, value) {
    try {
      // Add a timestamp and random salt to the value
      const timestamp = Date.now();
      const salt = Math.random().toString(36).substring(2, 10);
      const valueWithMeta = JSON.stringify({
        v: value,
        t: timestamp,
        s: salt
      });
      
      // Encrypt the value with the app secret
      const encryptedValue = EnhancedSecurity.encryptData(valueWithMeta, this.appSecret);
      
      // Store in localStorage with a prefix to make it less obvious
      localStorage.setItem(key, encryptedValue);
      
      // Also store a verification hash in sessionStorage as a second factor
      // This makes it harder to copy cookies between tabs
      const verificationHash = EnhancedSecurity.hashData(`${value}${timestamp}${salt}`);
      sessionStorage.setItem(`${key}_verify`, verificationHash);
    } catch (e) {
      // Failed to store secure value
    }
  }

  /**
   * Get a securely stored value from localStorage with verification
   * @param {string} key Storage key
   * @returns {string|null} Decrypted value or null
   */
  getSecureValue(key) {
    try {
      const encryptedValue = localStorage.getItem(key);
      if (!encryptedValue) return null;
      
      // Decrypt the value
      const decryptedJson = EnhancedSecurity.decryptData(encryptedValue, this.appSecret);
      if (!decryptedJson) return null;
      
      // Parse the JSON with metadata
      const parsed = JSON.parse(decryptedJson);
      if (!parsed || !parsed.v || !parsed.t || !parsed.s) return null;
      
      // Check if the value is expired (optional, 24 hour max age)
      const maxAge = 24 * 60 * 60 * 1000; // 24 hours
      if (Date.now() - parsed.t > maxAge) {
        localStorage.removeItem(key);
        sessionStorage.removeItem(`${key}_verify`);
        return null;
      }
      
      // Verify against the sessionStorage hash
      const verificationHash = sessionStorage.getItem(`${key}_verify`);
      const expectedHash = EnhancedSecurity.hashData(`${parsed.v}${parsed.t}${parsed.s}`);
      
      if (!verificationHash || verificationHash !== expectedHash) {
        return null;
      }
      
      return parsed.v;
    } catch (e) {
      return null;
    }
  }

  /**
   * Create a new session after successful login
   * @param {string} userId User identifier
   * @param {string} email User email
   * @returns {Promise<boolean>} Success status
   */
  async createSession(userId, email) {
    try {
      // Generate a unique session ID
      const sessionId = this.generateSessionId();
      
      // Get device fingerprint
      const fingerprint = await EnhancedSecurity.generateAdvancedFingerprint();
      
      // Store session in Redis via API
      const response = await fetch(this.apiEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'create',
          userId,
          sessionId,
          fingerprint,
          email
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        // Store session info locally (encrypted)
        this.storeSecureValue(this.sessionIdKey, sessionId);
        this.storeSecureValue(this.userIdKey, userId);
        return true;
      }
      
      return false;
    } catch (error) {
      return false;
    }
  }

  /**
   * Validate the current session
   * @returns {Promise<boolean>} Whether session is valid
   */
  async validateSession() {
    try {
      const sessionId = this.getSecureValue(this.sessionIdKey);
      const userId = this.getSecureValue(this.userIdKey);
      
      if (!sessionId || !userId) {
        return false;
      }
      
      // Get current fingerprint
      const fingerprint = await EnhancedSecurity.generateAdvancedFingerprint();
      
      // Validate with server
      const response = await fetch(this.apiEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'validate',
          userId,
          sessionId,
          fingerprint
        })
      });
      
      const result = await response.json();
      return result.valid === true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Invalidate the current session (logout)
   * @returns {Promise<boolean>} Success status
   */
  async invalidateSession() {
    try {
      const sessionId = this.getSecureValue(this.sessionIdKey);
      const userId = this.getSecureValue(this.userIdKey);
      
      if (!sessionId || !userId) {
        return true; // No session to invalidate
      }
      console.log('Session invalidation started');
      // Invalidate on server
      await fetch(this.apiEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'invalidate',
          userId,
          sessionId
        })
      });
      console.log('Session invalidated');
      // Clear local storage
      localStorage.removeItem(this.sessionIdKey);
      localStorage.removeItem(this.userIdKey);
      
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Generate a unique session ID
   * @returns {string} Session ID
   */
  generateSessionId() {
    // Use crypto API for better randomness if available
    if (window.crypto && window.crypto.getRandomValues) {
      const array = new Uint8Array(16);
      window.crypto.getRandomValues(array);
      const randomStr = Array.from(array)
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
      
      return `${Date.now().toString(36)}-${randomStr}`;
    }
    
    // Fallback to less secure but still reasonable method
    return Date.now().toString(36) + Math.random().toString(36).substring(2);
  }

  /**
   * Get the current session ID
   * @returns {string|null} Session ID or null if not logged in
   */
  getSessionId() {
    return this.getSecureValue(this.sessionIdKey);
  }

  /**
   * Get the current user ID
   * @returns {string|null} User ID or null if not logged in
   */
  getUserId() {
    return this.getSecureValue(this.userIdKey);
  }
}

export default new SessionManager();